/**
 * chain-eos/mechat/crypto.ts — ECIES encryption for `mechatmechat` messages.
 *
 * Wire-compatible with the PayCash wallet / NewChat SDK scheme:
 *
 *   1. ephemeral secp256k1 keypair
 *   2. ECDH with the recipient's public key -> shared X coordinate
 *   3. SHA-512(shared)  -> first 32 bytes = AES key, last 32 bytes = HMAC key
 *   4. AES-256-CBC (PKCS#7) over the UTF-8 plaintext, random 16-byte IV
 *   5. HMAC-SHA256(iv || ephemPubCompressed || cipherText) with the HMAC key
 *
 * Verified bidirectionally against the reference implementation
 * (`@newchat/new.chat-sdk@1.0.13`, MIT) — 600 round-trips in both directions plus
 * the frozen vectors in `__tests__/crypto.test.ts`.
 *
 * Dependencies: `elliptic` (already in the tree — @wharfkit/antelope signs with it)
 * and WebCrypto for SHA-512 / HMAC / AES-CBC. No crypto-js, no eosjs, no new bundle
 * weight.
 *
 * ── Two traps that silently break interop ────────────────────────────────────
 *
 * A. The MAC covers the COMPRESSED (33-byte) ephemeral public key. Classic
 *    `eccrypto`-style code uses the uncompressed 65-byte form and produces a MAC
 *    the PayCash app rejects.
 *
 * B. The shared secret is NOT the zero-padded 32-byte X coordinate. elliptic's
 *    `derive()` returns a BN, `BN.toString('hex')` strips leading zero NIBBLES, and
 *    `Buffer.from(hex, 'hex')` then drops a trailing half-byte when the length is
 *    odd. About 6% of keypairs produce a secret shorter than 32 bytes, so a "clean"
 *    reimplementation decrypts ~94% of messages and fails mysteriously on the rest.
 *    We keep elliptic in this path precisely so the quirk reproduces itself.
 *
 *    For the same reason, do NOT switch to `PrivateKey.sharedSecret()` from
 *    @wharfkit/antelope: it hashes the padded X coordinate and diverges on exactly
 *    those keypairs (measured: 19 of 300).
 */

import elliptic from 'elliptic'
import { PrivateKey, PublicKey } from '@wharfkit/antelope'

const curve = new elliptic.ec('secp256k1')

/** Encrypted payload — field names match the `senddm` action's ABI fields. */
export interface EncryptedMessage {
  /** 16-byte AES-CBC initialisation vector, hex. */
  iv: string
  /** Ephemeral public key in EOS format (`PUB_K1_...`). */
  ephemPubKey: string
  /** AES-256-CBC ciphertext, hex. */
  cipherText: string
  /** HMAC-SHA256 tag, hex (32 bytes -> `checksum256`). */
  mac: string
}

function webcrypto(): Crypto {
  const c = globalThis.crypto
  if (!c?.subtle) {
    throw new Error('[mechat] WebCrypto unavailable — encryption needs a secure context')
  }
  return c
}

function toHex(bytes: Uint8Array): string {
  let out = ''
  for (const b of bytes) out += b.toString(16).padStart(2, '0')
  return out
}

function fromHex(hex: string): Uint8Array {
  const clean = hex.length % 2 === 0 ? hex : hex.slice(0, -1)
  const out = new Uint8Array(clean.length / 2)
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16)
  return out
}

function concatBytes(...arrays: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(arrays.reduce((n, a) => n + a.length, 0))
  let offset = 0
  for (const a of arrays) {
    out.set(a, offset)
    offset += a.length
  }
  return out
}

/** ECDH shared secret — see trap B above; the truncation is deliberate. */
function sharedSecretBytes(privKey: Uint8Array, pubKey: Uint8Array): Uint8Array {
  const priv = curve.keyFromPrivate(privKey)
  const pub = curve.keyFromPublic(pubKey).getPublic()
  return fromHex(priv.derive(pub).toString('hex'))
}

async function deriveKeys(shared: Uint8Array): Promise<{ aesKey: Uint8Array; macKey: Uint8Array }> {
  const digest = new Uint8Array(await webcrypto().subtle.digest('SHA-512', shared as BufferSource))
  return { aesKey: digest.slice(0, 32), macKey: digest.slice(32, 64) }
}

async function hmacSha256(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const { subtle } = webcrypto()
  const imported = await subtle.importKey(
    'raw',
    key as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
  return new Uint8Array(await subtle.sign('HMAC', imported, data as BufferSource))
}

async function aesCbc(
  mode: 'encrypt' | 'decrypt',
  key: Uint8Array,
  iv: Uint8Array,
  data: Uint8Array,
): Promise<Uint8Array> {
  const { subtle } = webcrypto()
  const imported = await subtle.importKey('raw', key as BufferSource, { name: 'AES-CBC' }, false, [
    'encrypt',
    'decrypt',
  ])
  const out = await subtle[mode](
    { name: 'AES-CBC', iv: iv as BufferSource },
    imported,
    data as BufferSource,
  )
  return new Uint8Array(out)
}

/** Length-safe, branch-free-ish comparison of two hex MACs. */
function macEquals(a: string, b: string): boolean {
  const x = a.toLowerCase()
  const y = b.toLowerCase()
  if (x.length !== y.length) return false
  let diff = 0
  for (let i = 0; i < x.length; i++) diff |= x.charCodeAt(i) ^ y.charCodeAt(i)
  return diff === 0
}

/**
 * Encrypts `plainText` for the owner of `recipientPubKey`.
 * Accepts both `PUB_K1_...` and legacy `EOS...` public key formats.
 */
export async function encryptMessage(
  plainText: string,
  recipientPubKey: string | PublicKey,
): Promise<EncryptedMessage> {
  const recipient = PublicKey.from(recipientPubKey)
  const ephem = curve.genKeyPair()
  const ephemPriv = new Uint8Array(ephem.getPrivate().toArray('be', 32))
  const ephemPub = new Uint8Array(ephem.getPublic(true, 'array') as number[]) // compressed, 33 bytes

  const { aesKey, macKey } = await deriveKeys(sharedSecretBytes(ephemPriv, recipient.data.array))
  const iv = new Uint8Array(16)
  webcrypto().getRandomValues(iv)
  const cipher = await aesCbc('encrypt', aesKey, iv, new TextEncoder().encode(plainText))
  const mac = await hmacSha256(macKey, concatBytes(iv, ephemPub, cipher))

  return {
    iv: toHex(iv),
    ephemPubKey: PublicKey.from({ type: 'K1', compressed: ephemPub }).toString(),
    cipherText: toHex(cipher),
    mac: toHex(mac),
  }
}

/**
 * Decrypts a message addressed to the owner of `recipientPrivKey`.
 * Throws `Invalid mac` when the payload was encrypted to a different key or tampered with.
 */
export async function decryptMessage(
  message: EncryptedMessage,
  recipientPrivKey: string | PrivateKey,
): Promise<string> {
  const priv = PrivateKey.from(recipientPrivKey)
  const ephemPub = PublicKey.from(message.ephemPubKey).data.array
  const iv = fromHex(message.iv)
  const cipher = fromHex(message.cipherText)

  const { aesKey, macKey } = await deriveKeys(sharedSecretBytes(priv.data.array, ephemPub))
  const realMac = toHex(await hmacSha256(macKey, concatBytes(iv, ephemPub, cipher)))
  if (!macEquals(realMac, message.mac)) throw new Error('Invalid mac')

  const plain = await aesCbc('decrypt', aesKey, iv, cipher)
  return stripPayCashPlaintextWrapper(new TextDecoder().decode(plain))
}

/**
 * Decrypts a file the PayCash app attached to a chat message (its `<file>url;
 * name;size;ext</file>` tag — see `attachments.ts`). PayCash's `ChatManager.
 * uploadFile()` (decompiled from the official APK, 2026-09-06) encrypts the raw
 * file bytes with AES-256-CBC/PKCS#7 using the SAME derived key and the SAME
 * random IV it then reuses to encrypt the message envelope itself — so the `iv`
 * and `ephemPubKey` already on the `senddm` action (i.e. on `message`) are
 * exactly what's needed here too. No separate key/IV is transmitted anywhere.
 * Documented in docs/LEARNINGS.md.
 */
export async function decryptPayCashFile(
  encryptedFileBytes: Uint8Array,
  message: Pick<EncryptedMessage, 'iv' | 'ephemPubKey'>,
  recipientPrivKey: string | PrivateKey,
): Promise<Uint8Array> {
  const priv = PrivateKey.from(recipientPrivKey)
  const ephemPub = PublicKey.from(message.ephemPubKey).data.array
  const iv = fromHex(message.iv)
  const { aesKey } = await deriveKeys(sharedSecretBytes(priv.data.array, ephemPub))
  return aesCbc('decrypt', aesKey, iv, encryptedFileBytes)
}

/**
 * The send-side counterpart of `decryptPayCashFile`. Encrypts a file for
 * `recipientPubKey` exactly the way the PayCash app does when attaching one to a
 * chat message (decompiled from the official APK, 2026-09-06 —
 * `ChatManager$uploadFile$2`).
 *
 * The app generates ONE ephemeral keypair and ONE random IV, AES-256-CBC-encrypts
 * the raw file bytes with them for the upload, and then REUSES that same key + IV
 * to encrypt the `<file>url;name;size;ext</file>` envelope it sends as the
 * message. That reuse is the whole reason a recipient can decrypt the file from
 * the message's own `iv`/`ephemPubKey`.
 *
 * Usage: upload `encryptedFile`, then call `seal(tag)` with the finished
 * `<file>…</file>` tag (now that the upload URL is known) to get the senddm
 * envelope sealed under the same ephemeral context.
 */
export interface PayCashFileSeal {
  /** AES-256-CBC(PKCS#7) ciphertext of the raw file — the multipart upload body. */
  encryptedFile: Uint8Array
  /** Seals the `<file>…</file>` message under the same ephemeral key + IV. */
  seal(messageText: string): Promise<EncryptedMessage>
}

export async function encryptPayCashFile(
  fileBytes: Uint8Array,
  recipientPubKey: string | PublicKey,
): Promise<PayCashFileSeal> {
  const recipient = PublicKey.from(recipientPubKey)
  const ephem = curve.genKeyPair()
  const ephemPriv = new Uint8Array(ephem.getPrivate().toArray('be', 32))
  const ephemPub = new Uint8Array(ephem.getPublic(true, 'array') as number[]) // compressed, 33 bytes

  const { aesKey, macKey } = await deriveKeys(sharedSecretBytes(ephemPriv, recipient.data.array))
  const iv = new Uint8Array(16)
  webcrypto().getRandomValues(iv)

  const encryptedFile = await aesCbc('encrypt', aesKey, iv, fileBytes)
  const ephemPubKeyStr = PublicKey.from({ type: 'K1', compressed: ephemPub }).toString()
  const ivHex = toHex(iv)

  return {
    encryptedFile,
    async seal(messageText: string): Promise<EncryptedMessage> {
      const cipher = await aesCbc('encrypt', aesKey, iv, new TextEncoder().encode(messageText))
      const mac = await hmacSha256(macKey, concatBytes(iv, ephemPub, cipher))
      return { iv: ivHex, ephemPubKey: ephemPubKeyStr, cipherText: toHex(cipher), mac: toHex(mac) }
    },
  }
}

/** PayCash wraps chat body as `<plaintext>...</plaintext>` — strip for UI. */
export function stripPayCashPlaintextWrapper(text: string): string {
  const trimmed = text.trim()
  const m = trimmed.match(/^<plaintext>([\s\S]*)<\/plaintext>$/i)
  return m ? m[1].replace(/\s+$/u, '') : text
}

/** Exposed for tests and the verification script only. */
export const __mechatInternals = { sharedSecretBytes, deriveKeys }
